/**
 * ============================================================================
 * TRANSACTION ANALYTICS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for displaying transaction analytics
 */

import React, { useEffect, useState } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import AnalyticsCard from './AnalyticsCard';
import SimpleChart from './SimpleChart';
import Select from '../common/Select';

/**
 * Transaction Analytics Component
 * @param {Object} props
 * @param {string} props.timeRange - Time range filter
 * @returns {JSX.Element}
 */
const TransactionAnalytics = ({ timeRange = '30d' }) => {
  const { loadTransactionAnalytics, transactionAnalytics, loading } = useAnalytics();
  const { showError } = useToast();
  const { formatCurrency, formatNumber, language } = useI18n();
  const [selectedRange, setSelectedRange] = useState(timeRange);

  useEffect(() => {
    const loadData = async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        
        switch (selectedRange) {
          case '7d':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case '30d':
            startDate.setDate(startDate.getDate() - 30);
            break;
          case '90d':
            startDate.setDate(startDate.getDate() - 90);
            break;
          case '1y':
            startDate.setFullYear(startDate.getFullYear() - 1);
            break;
          default:
            startDate.setDate(startDate.getDate() - 30);
        }

        await loadTransactionAnalytics({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupBy: selectedRange === '7d' ? 'day' : selectedRange === '30d' ? 'day' : 'week',
        });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des statistiques' : 'Failed to load statistics')
        );
      }
    };

    loadData();
  }, [selectedRange, loadTransactionAnalytics, showError, language]);

  if (loading && !transactionAnalytics) {
    return (
      <Card variant="glass" className="p-12">
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  const analytics = transactionAnalytics?.data || transactionAnalytics || {};
  const stats = analytics.stats || {};
  const chartData = analytics.chartData || [];

  const chartDataFormatted = chartData.map((item) => ({
    label: item.label || item.date || '',
    value: item.count || item.value || 0,
    color: 'from-blue-500 to-cyan-500',
  }));

  return (
    <div className="space-y-6">
      {/* Header with Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-gray-900 dark:text-white">
          📊 {language === 'fr' ? 'Analytique des Transactions' : 'Transaction Analytics'}
        </h2>
        <Select
          value={selectedRange}
          onChange={(e) => setSelectedRange(e.target.value)}
          options={[
            { value: '7d', label: language === 'fr' ? '7 jours' : '7 Days' },
            { value: '30d', label: language === 'fr' ? '30 jours' : '30 Days' },
            { value: '90d', label: language === 'fr' ? '90 jours' : '90 Days' },
            { value: '1y', label: language === 'fr' ? '1 an' : '1 Year' },
          ]}
          className="w-40"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <AnalyticsCard
          title={language === 'fr' ? 'Total Transactions' : 'Total Transactions'}
          value={formatNumber(stats.total || 0)}
          icon="📦"
          color="from-blue-500 to-cyan-500"
          trend={stats.totalTrend}
          trendValue={stats.totalTrendValue}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Montant Total' : 'Total Amount'}
          value={formatCurrency(stats.totalAmount || 0, 'XAF')}
          icon="💰"
          color="from-green-500 to-emerald-500"
          trend={stats.amountTrend}
          trendValue={stats.amountTrendValue}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Transactions Réussies' : 'Successful'}
          value={formatNumber(stats.successful || 0)}
          icon="✅"
          color="from-green-500 to-teal-500"
          trend={stats.successfulTrend}
          trendValue={stats.successfulTrendValue}
        />
        <AnalyticsCard
          title={language === 'fr' ? 'Taux de Réussite' : 'Success Rate'}
          value={`${Number(stats.successRate ?? 0).toFixed(1)}%`}
          icon="📈"
          color="from-purple-500 to-pink-500"
          trend={stats.rateTrend}
          trendValue={stats.rateTrendValue}
        />
      </div>

      {/* Chart */}
      {chartDataFormatted.length > 0 && (
        <SimpleChart
          title={language === 'fr' ? 'Transactions par Période' : 'Transactions Over Time'}
          data={chartDataFormatted}
          type="bar"
          height="250px"
        />
      )}
    </div>
  );
};

export default TransactionAnalytics;
